import { Component, Input } from '@angular/core';

/**
 * Read-only "resolved reference" display: a bordered, disabled field showing a value resolved from
 * an id (e.g. an affiliated organization's name), with an optional trailing action - a "Select"
 * button - projected via <ng-content> after the field.
 *
 * This exact shape (label + disabled bordered value + optional picker button) was being hand-rolled
 * per consumer on organization-details and tank-setup's General form, and each copy drifted from the
 * others in a different way: wrong Bootstrap breakpoint class (col-3 instead of col-md-3), a label
 * missing the classes/styling every other field's label gets, Bootstrap's col-md-N max-width fighting
 * a CSS grid cell it was never designed for, and hidden sibling fields silently consuming grid
 * positions and shifting this field sideways. One correct implementation here means every consumer
 * gets the right label styling, control height, and responsive label-left layout for free, instead of
 * re-deriving (and re-breaking) it.
 *
 * Deliberately does NOT manage a reactive form control - the field never actually needed one (see the
 * read-only note on TankSetupGeneralFormComponent). If a consumer needs the underlying id to
 * round-trip through form submission, keep a plain `<app-form-field type="hidden">` beside this
 * component, the way tank-setup-general-form.component.html does.
 */
@Component({
  selector: 'app-reference-display-field',
  templateUrl: './reference-display-field.component.html',
  styleUrls: ['./reference-display-field.component.scss'],
})
export class ReferenceDisplayFieldComponent {
  /** Translation key for the label, matching app-form-field's fieldLabel convention. */
  @Input() label: string;

  /**
   * Untranslated label text, used as-is instead of running `label` through the translate pipe.
   * Escape hatch for callers that never had a real translation key for this field (the
   * organization-details usage predates this component and rendered plain English text) - passing
   * an English string through `label` instead would surface as the literal `[key]` fallback text
   * from CommonMissingTranslationHandler the moment no matching translation entry exists.
   */
  @Input() rawLabel: string;

  /** Resolved display value. Falls back to noneLabel when null/empty. */
  @Input() value: string | null | undefined;

  /** Shown when value is null, undefined, or empty. */
  @Input() noneLabel = 'None';

  /** Pairs the label's `for` with the input's `id` for accessibility; also used as the DOM id. */
  @Input() inputId: string;
}
